// ============================================
// Chat Module - List Management (ES6)
// 이벤트 위임 패턴으로 메모리 누수 방지
// ============================================

import { getCurrentUser, getCurrentRoomId } from '../app/state.js';
import { loadSession } from './load.js';
import { openRoomActionModal } from './actions.js';
import { escapeHtml, formatChatDate } from '../utils.js';
import { getCachedImage } from '../image.js';
import { useChatStatusStore } from '../../stores/chatStatusStore.js';
import { showSessionModal } from './session-modal.js';

// ============================================
// 채팅 목록 캐시 상태
// ============================================
let cachedChats = null;
let chatCacheTime = 0;
const CHAT_CACHE_DURATION = 30000; // 30초 캐시
const LONG_PRESS_DURATION = 500; // 0.5초

// ============================================
// 채팅 목록 필터 상태 (로컬)
// ============================================
let chatFilterMode = 'all';
let showHiddenChats = false;

// ============================================
// Long Press 이벤트 위임 상태
// ============================================
let longPressTimer = null;
let longPressStartX = 0;
let longPressStartY = 0;
let isChatListListenerSetup = false;

// ============================================
// 실시간 리스너 상태
// ============================================
let chatListUnsubscribe = null;

// ============================================
// Long Press 이벤트 위임 설정 (한 번만 호출)
// ============================================

export function initChatListLongPress() {
  if (isChatListListenerSetup) return;

  const chatList = document.getElementById('chatList');
  if (!chatList) return;

  // 터치 이벤트 (모바일)
  chatList.addEventListener('touchstart', handleListTouchStart, { passive: true });
  chatList.addEventListener('touchmove', handleListTouchMove, { passive: true });
  chatList.addEventListener('touchend', handleListTouchEnd, { passive: true });
  chatList.addEventListener('touchcancel', handleListTouchEnd, { passive: true });

  // 컨텍스트 메뉴 (데스크톱 우클릭)
  chatList.addEventListener('contextmenu', handleListContextMenu);

  isChatListListenerSetup = true;
}

function handleListTouchStart(e) {
  const chatItem = e.target.closest('.chat-item');
  if (!chatItem) return;

  longPressStartX = e.touches[0].clientX;
  longPressStartY = e.touches[0].clientY;

  const chatId = chatItem.dataset.chatId;
  const roomId = chatItem.dataset.roomId;
  const isRoom = chatItem.dataset.isRoom === 'true';
  const title = chatItem.dataset.title || '새 채팅';
  const isHidden = chatItem.dataset.hidden === 'true';

  longPressTimer = setTimeout(() => {
    if (isRoom && roomId) {
      openRoomActionModal(roomId, title, isHidden);
    } else {
      openChatActionModal(chatId, title, isHidden);
    }
  }, LONG_PRESS_DURATION);
}

function handleListTouchMove(e) {
  if (!longPressTimer) return;

  const moveX = Math.abs(e.touches[0].clientX - longPressStartX);
  const moveY = Math.abs(e.touches[0].clientY - longPressStartY);

  if (moveX > 10 || moveY > 10) {
    clearTimeout(longPressTimer);
    longPressTimer = null;
  }
}

function handleListTouchEnd() {
  if (longPressTimer) {
    clearTimeout(longPressTimer);
    longPressTimer = null;
  }
}

function handleListContextMenu(e) {
  const chatItem = e.target.closest('.chat-item');
  if (!chatItem) return;

  e.preventDefault();

  const chatId = chatItem.dataset.chatId;
  const roomId = chatItem.dataset.roomId;
  const isRoom = chatItem.dataset.isRoom === 'true';
  const title = chatItem.dataset.title || '새 채팅';
  const isHidden = chatItem.dataset.hidden === 'true';

  if (isRoom && roomId) {
    openRoomActionModal(roomId, title, isHidden);
  } else {
    openChatActionModal(chatId, title, isHidden);
  }
}

// ============================================
// 채팅 목록 로딩
// ============================================

export async function loadChatList(retryCount = 0, forceRefresh = false) {
  const chatList = document.getElementById('chatList');
  if (!chatList) return; // DOM 요소가 없으면 early return

  const maxRetries = 3;

  // 캐시가 유효하고 이미 렌더링된 경우 스킵 (이미지 깜빡임 방지)
  const now = Date.now();
  if (!forceRefresh && cachedChats && (now - chatCacheTime < CHAT_CACHE_DURATION)) {
    // 이미 렌더링된 경우 active 상태와 배지만 업데이트
    if (chatList.children.length > 0 && !chatList.querySelector('.spinner')) {
      updateChatListActiveState();
      refreshChatListBadges(); // pending/unread 배지 갱신
      return;
    }
    renderChatList(cachedChats);
    return;
  }

  // 로딩 표시 (스피너)
  if (retryCount === 0) {
    chatList.innerHTML = '<div style="display: flex; justify-content: center; padding: 40px;"><div class="spinner"></div></div>';
  }

  // Firestore 인스턴스
  const db = firebase.firestore();

  let chats = [];
  try {
    const userId = getCurrentUser()?.uid;
    if (!userId) {
      chatList.innerHTML = '<div style="padding: 20px; color: var(--text-secondary); text-align: center; font-size: 13px;">로그인이 필요합니다</div>';
      return;
    }

    // chatRooms만 로드 (레거시 chats 컬렉션 제거됨)
    const roomsSnap = await db.collection('users').doc(userId)
      .collection('chatRooms')
      .orderBy('updatedAt', 'desc')
      .get();

    // chatRooms → 리스트 아이템 변환
    chats = roomsSnap.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        roomId: doc.id,
        sessionId: data.lastSessionId || null,
        sessionCount: data.sessionCount || 1,
        isRoom: true,
        title: data.title || '새 채팅',
        mode: data.mode || 'character',
        hidden: data.hidden || false,
        published: data.published || false,
        publishedId: data.publishedId || null,
        character: data.character ? {
          name: data.character.name,
          imageUrl: data.character.imageUrl || null,
          thumbUrl: data.character.thumbUrl || data.character.smallThumbUrl || null
        } : null,
        characters: data.characters ? data.characters.filter(c => c).map(c => ({
          name: c.name,
          imageUrl: c.imageUrl || null,
          thumbUrl: c.thumbUrl || c.smallThumbUrl || null
        })) : null,
        images: data.images || null,
        lastMessage: data.lastMessage?.content || data.lastMessage || null,
        userProfileName: data.userProfile?.name || '유저',
        updatedAt: data.updatedAt?.toDate?.()?.toISOString() || null,
        createdAt: data.createdAt?.toDate?.()?.toISOString() || null
      };
    });

    // 캐시 저장
    cachedChats = chats;
    chatCacheTime = now;
  } catch (e) {
    console.error(`loadChatList error (attempt ${retryCount + 1}):`, e);
    if (retryCount < maxRetries) {
      await new Promise(resolve => setTimeout(resolve, 1000 * (retryCount + 1)));
      return loadChatList(retryCount + 1, forceRefresh);
    }
    chatList.innerHTML = '<div style="padding: 20px; color: var(--text-secondary); text-align: center; font-size: 13px;">로딩 실패. <span style="color: var(--accent); cursor: pointer;" data-action="retry">다시 시도</span></div>';
    chatList.querySelector('[data-action="retry"]')?.addEventListener('click', () => loadChatList(0, true));
    return;
  }

  renderChatList(chats);
}

// ============================================
// 캐시 관리
// ============================================

export function invalidateChatCache() {
  cachedChats = null;
  chatCacheTime = 0;
}

export function removeChatFromList(chatId) {
  // 1. DOM에서 제거
  const chatItem = document.querySelector(`.chat-item[data-chat-id="${chatId}"]`);
  if (chatItem) chatItem.remove();

  // 2. 캐시에서 제거
  if (cachedChats) {
    cachedChats = cachedChats.filter(c => c.id !== chatId);
  }
}

export function removeRoomFromList(roomId) {
  // 1. DOM에서 제거 (room-id로 검색)
  const chatItem = document.querySelector(`.chat-item[data-room-id="${roomId}"]`);
  if (chatItem) chatItem.remove();

  // 2. 캐시에서 제거
  if (cachedChats) {
    cachedChats = cachedChats.filter(c => c.roomId !== roomId);
  }
}

// ============================================
// 채팅 목록 active 상태 업데이트
// ============================================

function updateChatListActiveState() {
  const chatList = document.getElementById('chatList');
  const currentRoomId = getCurrentRoomId();

  chatList.querySelectorAll('.chat-item').forEach(item => {
    // 세션 구조에서는 모든 항목이 room
    const isActive = item.dataset.roomId === currentRoomId;

    if (isActive) {
      item.classList.add('active');
    } else {
      item.classList.remove('active');
    }
  });
}

// ============================================
// 채팅 목록 렌더링
// ============================================

export function renderChatList(chats) {
  const chatList = document.getElementById('chatList');
  chatList.innerHTML = '';

  if (chats.length === 0) {
    chatList.innerHTML = '<div style="padding: 20px; color: var(--text-secondary); text-align: center; font-size: 13px;">채팅이 없습니다</div>';
    return;
  }

  // chatFilterMode, showHiddenChats는 모듈 상단에 정의된 로컬 상태 사용
  let hasVisibleChats = false;

  chats.forEach(chat => {
    // Filter by mode
    if (chatFilterMode !== 'all' && chat.mode !== chatFilterMode) {
      return;
    }

    // Filter hidden chats
    if (chat.hidden && !showHiddenChats) {
      return;
    }

    hasVisibleChats = true;

    const modeBadge = chat.mode === 'story'
      ? '<span class="chat-mode-badge story">스토리</span>'
      : '<span class="chat-mode-badge character">점술가</span>';

    // 상담 배지 (2개 이상일 때만 표시)
    const sessionBadge = (chat.isRoom && chat.sessionCount >= 2)
      ? `<span class="session-count-badge">상담 ${chat.sessionCount}</span>`
      : '';

    // 대기 중 상태 확인 (sessionId 기준)
    const chatStatus = useChatStatusStore.getState();
    const isPending = chatStatus.isPending(chat.sessionId);

    // 배지 HTML
    const pendingIndicator = isPending ? '<div class="pending-indicator"><div class="spinner-small"></div></div>' : '';

    // 캐릭터 이미지 URL 가져오기 (캐싱 적용, 썸네일 우선)
    // 구조: chat-avatar-wrapper > (img-container + badges) - onerror가 배지를 삭제하지 않도록 분리
    let avatarHTML = '';
    const charImageUrl = chat.character?.thumbUrl || chat.character?.imageUrl;
    const storyImageUrl = chat.characters?.[0]?.thumbUrl || chat.characters?.[0]?.imageUrl;
    if (chat.mode === 'character' && charImageUrl) {
      const cachedUrl = getCachedImage(charImageUrl);
      avatarHTML = `<div class="chat-avatar-wrapper"><div class="img-loading-wrapper"><div class="spinner"></div><img src="${cachedUrl}" class="chat-item-avatar" alt="${chat.character.name}" onload="this.classList.add('loaded');this.parentElement&&this.parentElement.classList.add('loaded')" onerror="if(this.parentElement)this.parentElement.innerHTML='<div class=\\'chat-avatar-placeholder\\'><svg width=\\'24\\' height=\\'24\\' viewBox=\\'0 0 24 24\\' fill=\\'none\\' stroke=\\'currentColor\\' stroke-width=\\'2\\'><path d=\\'M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2\\'/><circle cx=\\'12\\' cy=\\'7\\' r=\\'4\\'/></svg></div>'"></div>${pendingIndicator}</div>`;
    } else if (chat.mode === 'story' && storyImageUrl) {
      const cachedUrl = getCachedImage(storyImageUrl);
      avatarHTML = `<div class="chat-avatar-wrapper"><div class="img-loading-wrapper"><div class="spinner"></div><img src="${cachedUrl}" class="chat-item-avatar" alt="${chat.characters[0].name}" onload="this.classList.add('loaded');this.parentElement&&this.parentElement.classList.add('loaded')" onerror="if(this.parentElement)this.parentElement.innerHTML='<div class=\\'chat-avatar-placeholder\\'><svg width=\\'24\\' height=\\'24\\' viewBox=\\'0 0 24 24\\' fill=\\'none\\' stroke=\\'currentColor\\' stroke-width=\\'2\\'><path d=\\'M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253\\'/></svg></div>'"></div>${pendingIndicator}</div>`;
    } else {
      // 기본 아바타
      const defaultIcon = chat.mode === 'story'
        ? '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"/></svg>'
        : '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>';
      avatarHTML = `<div class="chat-avatar-wrapper"><div class="chat-avatar-placeholder">${defaultIcon}</div>${pendingIndicator}</div>`;
    }

    // 캐릭터 이름 가져오기
    const charName = chat.mode === 'character'
      ? (chat.character?.name || chat.title || '점술가')
      : (chat.characters?.[0]?.name || chat.title || '스토리');

    // 마지막 메시지 처리 (한 줄로 자르기)
    // pending 상태면 로딩 애니메이션 표시
    let lastMsgPreview = '';
    if (isPending) {
      lastMsgPreview = '<span class="chat-list-loading"><span></span><span></span><span></span></span>';
    } else if (chat.lastMessage) {
      const userName = chat.userProfileName || '유저';
      // 특수 태그 제거하고 텍스트만 추출
      let cleanMsg = chat.lastMessage
        .replace(/\{I\}[^\s{}\n]+/g, '[이미지]')  // 이미지 태그
        .replace(/@silently[\s\S]*?@\/silently/g, '')  // silently 블록
        .replace(/\{[A-U]\}/g, '')  // 단일 문자 마커 제거 ({N}, {C}, {S}, {T} 등)
        .replace(/\{\{user\}\}/gi, userName)  // {{user}} 마커를 프로필 이름으로
        .replace(/\{\{char\}\}/gi, charName)  // {{char}} 마커를 캐릭터 이름으로
        .replace(/\{\{[^}]+\}\}/g, '')  // 기타 {{...}} 마커 제거
        .replace(/\*\*([^*]+)\*\*/g, '$1')  // 볼드
        .replace(/\*([^*]+)\*/g, '$1')  // 이탤릭
        .replace(/\n+/g, ' ')  // 줄바꿈을 공백으로
        .trim();
      lastMsgPreview = cleanMsg;
    }

    const div = document.createElement('div');
    div.className = `chat-item ${chat.hidden ? 'hidden-chat' : ''}`;
    div.dataset.chatId = chat.id;
    div.dataset.roomId = chat.roomId || '';
    div.dataset.sessionId = chat.sessionId || '';
    div.dataset.sessionCount = chat.sessionCount || '0';
    div.dataset.isRoom = chat.isRoom ? 'true' : 'false';
    div.dataset.hidden = chat.hidden ? 'true' : 'false';
    div.dataset.title = chat.title || '새 채팅'; // 이벤트 위임에서 사용
    div.innerHTML = `
      <div class="chat-item-info">
        ${avatarHTML}
        <div class="chat-item-text">
          <div class="chat-item-header">
            <div class="chat-item-name-row">
              <span class="chat-item-title">${escapeHtml(charName)}</span>
              ${modeBadge}
              ${sessionBadge}
            </div>
            <span class="chat-item-date">${formatChatDate(chat.updatedAt ? new Date(chat.updatedAt) : null)}</span>
          </div>
          ${lastMsgPreview ? `<div class="chat-item-preview">${lastMsgPreview}</div>` : ''}
        </div>
      </div>
    `;

    // Click handler (단순 클릭 - 이벤트 위임 불필요)
    div.querySelector('.chat-item-info').onclick = () => {
      // 세션 구조만 지원 (레거시 chats 제거됨)
      if (chat.sessionCount > 1) {
        // 여러 세션 → 세션 목록 모달
        showSessionModal(chat.roomId, chat.title);
      } else {
        // 단일 세션 → 바로 입장
        loadSession(chat.roomId, chat.sessionId);
      }
    };

    // Long press는 이벤트 위임으로 처리됨 (setupLongPress 제거됨)

    chatList.appendChild(div);
  });

  if (!hasVisibleChats) {
    const message = chatFilterMode === 'all'
      ? '채팅이 없습니다'
      : `${chatFilterMode === 'character' ? '점술가' : '스토리'} 채팅이 없습니다`;
    chatList.innerHTML = `<div style="padding: 20px; color: var(--text-secondary); text-align: center; font-size: 13px;">${message}</div>`;
  }
}

// ============================================
// 필터 함수
// ============================================

export function filterChats(mode) {
  chatFilterMode = mode;
  document.querySelectorAll('.chats-filter-btn').forEach(btn => {
    btn.classList.toggle('active', btn.dataset.chatFilter === mode);
  });
  loadChatList();
}

export function toggleShowHidden() {
  showHiddenChats = !showHiddenChats;
  const btn = document.getElementById('hiddenToggleBtn');
  if (btn) {
    btn.classList.toggle('active', showHiddenChats);
  }
  loadChatList();
}

// ============================================
// 실시간 리스너 (새 메시지 감지)
// ============================================

export function startChatListListener(firebaseUser = null) {
  // firebaseUser가 전달되면 사용, 아니면 store에서 가져옴
  const user = firebaseUser || getCurrentUser();
  if (!user || chatListUnsubscribe) return;

  const db = firebase.firestore();
  // chatRooms 컬렉션 리스닝 (레거시 chats 제거됨)
  chatListUnsubscribe = db.collection('users').doc(user.uid)
    .collection('chatRooms')
    .orderBy('updatedAt', 'desc')
    .onSnapshot(snapshot => {
      snapshot.docChanges().forEach(change => {
        if (change.type === 'modified') {
          const roomId = change.doc.id;
          const currentRoomId = getCurrentRoomId();
          const chatStatus = useChatStatusStore.getState();

          // 리스트 UI 업데이트
          updateChatItemBadge(roomId);
        }
      });

      // 캐시 무효화하고 리스트 갱신
      invalidateChatCache();
    }, error => {
      console.error('[ChatList] 실시간 리스너 에러:', error);
    });
}

export function stopChatListListener() {
  if (chatListUnsubscribe) {
    chatListUnsubscribe();
    chatListUnsubscribe = null;
  }
}

// 개별 채팅 아이템의 배지와 미리보기 업데이트 (전체 리렌더링 방지)
function updateChatItemBadge(roomId) {
  const chatItem = document.querySelector(`.chat-item[data-room-id="${roomId}"]`);
  if (!chatItem) return;

  const avatarWrapper = chatItem.querySelector('.chat-avatar-wrapper');
  if (!avatarWrapper) return;

  // sessionId 기준으로 pending 체크
  const sessionId = chatItem.dataset.sessionId;
  const chatStatus = useChatStatusStore.getState();
  const isPending = chatStatus.isPending(sessionId);

  // 기존 배지 제거
  avatarWrapper.querySelector('.pending-indicator')?.remove();

  // 새 배지 추가
  if (isPending) {
    avatarWrapper.insertAdjacentHTML('beforeend', '<div class="pending-indicator"><div class="spinner-small"></div></div>');
  }

  // 미리보기 영역에 로딩 애니메이션 표시/제거
  const preview = chatItem.querySelector('.chat-item-preview');
  if (preview) {
    const hasLoadingAnimation = preview.querySelector('.chat-list-loading');
    if (isPending && !hasLoadingAnimation) {
      // pending 상태인데 로딩 애니메이션이 없으면 추가
      preview.innerHTML = '<span class="chat-list-loading"><span></span><span></span><span></span></span>';
    } else if (!isPending && hasLoadingAnimation) {
      // pending 해제됨 - cachedChats에서 lastMessage 가져와서 미리보기 텍스트 교체
      const chat = cachedChats?.find(c => c.roomId === roomId);
      if (chat?.lastMessage) {
        const userName = chat.userProfileName || '유저';
        const cleanMsg = chat.lastMessage
          .replace(/\{I\}[^\s{}\n]+/g, '[이미지]')
          .replace(/@silently[\s\S]*?@\/silently/g, '')
          .replace(/\{N\}/g, '')
          .replace(/\{C\}/g, '')
          .replace(/\{\{user\}\}/gi, userName)
          .replace(/\*\*([^*]+)\*\*/g, '$1')
          .replace(/\*([^*]+)\*/g, '$1')
          .replace(/\n+/g, ' ')
          .trim();
        preview.textContent = cleanMsg;
      } else {
        // 캐시가 비어있으면 리스트 갱신 (Firestore 리스너가 캐시를 무효화함)
        loadChatList(0, true);
      }
    }
  }
}

// 채팅 리스트 배지 전체 업데이트
export function refreshChatListBadges() {
  const chatItems = document.querySelectorAll('.chat-item[data-room-id]');
  chatItems.forEach(item => {
    const roomId = item.dataset.roomId;
    if (roomId) updateChatItemBadge(roomId);
  });
}

// ============================================
// window 노출 제거됨 (2026-01-06)
// 모든 호출이 ES6 import로 처리됨
// ============================================
